import { Injectable } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

/**
 * Drop-in replacement for NgbModal that layers stacked modals deterministically
 * (PRO-1219). ng-bootstrap 6.x appends every window AND backdrop to <body> at
 * z-index 1050, so layering relies on DOM order and a backdrop can land over
 * its own window. Each opened modal is tagged with a stack-depth class that
 * `styles.css` maps to increasing z-indexes. See the repo CLAUDE.md for which
 * modules must re-provide this override. No constructor on purpose: under Ivy
 * a decorated subclass without one inherits the base class's injection,
 * avoiding the non-exported NgbModalStack.
 */
@Injectable()
export class StackingNgbModal extends NgbModal {
  /** Highest stack level with a z-index rule defined in styles.css. */
  private static readonly MAX_LEVEL = 4;

  /** Number of modals currently open. */
  private openModalCount = 0;

  open(content: any, options: NgbModalOptions = {}): NgbModalRef {
    const level = Math.min(this.openModalCount, StackingNgbModal.MAX_LEVEL);
    const stackedOptions: NgbModalOptions = {
      ...options,
      windowClass: this.appendClass(options.windowClass, `ngb-stacked-modal-window-${level}`),
      backdropClass: this.appendClass(options.backdropClass, `ngb-stacked-modal-backdrop-${level}`),
    };

    this.openModalCount++;
    const modalRef = super.open(content, stackedOptions);

    const release = () => { this.openModalCount = Math.max(0, this.openModalCount - 1); };
    modalRef.result.then(release, release); // settles on close OR dismiss (esc/backdrop)

    return modalRef;
  }

  private appendClass(existing: string | undefined, added: string): string {
    return existing ? `${existing} ${added}` : added;
  }
}
